import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Avatar, Col, Popover, Row, Spin } from 'antd';
import { UserName } from '../name_component/user/UserNameComponent';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { ContactAvatar } from '../contact_avatar/ContactAvatar';
import { connect } from 'react-redux';
import { withUserType } from '../withUserType';
import {
	contactsRestCrudThunksForBuyer,
	contactsRestCrudThunksForSupplier,
} from '../../thunks/contacts_thunks';
import { BLACKS_AND_WHITES } from '../../utils/DataFormatterUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';

const WorkOrderTechAssignedPill: React.FC<any> = ({ workOrder, getContacts, allContacts }) => {
	const [loadingPopoverContent, setLoadingPopoverContent] = useState(false);
	const [contacts, setContacts] = useState(null);

	useEffect(() => {
		if (nullSafeGet('lastServiceCall.leadTechnicianEmailContact', workOrder)) {
			//If there is a lastServiceCall with leadTechnicianEmailContact then the information already exists
			const contactList = [
				nullSafeGet('lastServiceCall.leadTechnicianEmailContact', workOrder),
				...nullSafeGetOrElse('lastServiceCall.additionalTechnicians', workOrder, []),
			];
			setContacts(
				contactList.reduce((acc, r) => {
					acc[r.email] = r;
					return acc;
				}, {})
			);
		}
	}, [workOrder]);

	const leadEmail = useMemo(
		() =>
			nullSafeGetOrElse(
				'lastServiceCall.leadTechnicianEmail',
				workOrder,
				nullSafeGet('lastServiceCallLeadTechnicianEmail', workOrder)
			),
		[workOrder]
	);
	const additionalEmails = useMemo(
		() =>
			nullSafeGetOrElse(
				'lastServiceCall.additionalTechnicianEmails',
				workOrder,
				nullSafeGetOrElse('lastServiceCallAdditionalTechnicianEmails', workOrder, [])
			),
		[workOrder]
	);

	const emails = useMemo(
		() => (leadEmail ? [leadEmail, ...additionalEmails] : []),
		[leadEmail, additionalEmails]
	);

	const leadName = useMemo(
		() => nullSafeGet('lastServiceCallLeadTechnicianName', workOrder),
		[workOrder]
	);
	const additionalNames = useMemo(
		() => nullSafeGetOrElse('lastServiceCallAdditionalTechnicianNames', workOrder, []),
		[workOrder]
	);

	const names = useMemo(
		() => (leadName ? [leadName, ...additionalNames] : []),
		[leadName, additionalNames]
	);

	const collectContacts = useCallback(() => {
		if (!emails.length) return;
		const existingContacts = emails.reduce((acc, email) => {
			const c = (contacts && contacts[email]) || (allContacts && allContacts[email]);
			if (c) acc[email] = c;
			return acc;
		}, {});

		const missingEmails = emails.filter((email) => !existingContacts[email]);

		// If we already have everything we need, just set from cache
		if (missingEmails.length === 0) {
			if (!contacts || Object.keys(contacts).length !== Object.keys(existingContacts).length) {
				setContacts(existingContacts);
			}
			return;
		}
		if (loadingPopoverContent) return;
		setLoadingPopoverContent(true);
		getContacts(missingEmails)
			.then((result) => {
				const fetched = result.reduce((acc, r) => {
					acc[r.email] = r;
					return acc;
				}, {});
				setContacts({ ...existingContacts, ...fetched });
			})
			.finally(() => setLoadingPopoverContent(false));
	}, [emails, contacts, allContacts, getContacts, loadingPopoverContent]);
	if (!workOrder || !leadEmail) {
		return null;
	}

	return (
		<Popover
			style={{ padding: 0 }}
			content={
				<Col
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: 8,
						minWidth: 225,
					}}
				>
					<Row style={{ borderBottom: '1px solid #D9D9D9' }}>
						<span>Lead Technician</span>
					</Row>
					<Row>
						{loadingPopoverContent || !contacts ? (
							<Spin />
						) : (
							<UserName contact={contacts[leadEmail]} allowHover={false} underline={false} />
						)}
					</Row>
					{nullSafeGetOrElse('length', additionalEmails, 0) > 0
						? [
								<Row style={{ borderBottom: '1px solid #D9D9D9', marginTop: 8 }}>
									<span>Additional Technician</span>
								</Row>,
								<Row>
									<Col
										style={{
											display: 'flex',
											flexDirection: 'column',
											gap: 8,
										}}
									>
										{loadingPopoverContent ? (
											<Spin />
										) : (
											additionalEmails.map(
												(email) =>
													contacts && (
														<UserName
															contact={contacts[email]}
															allowHover={false}
															underline={false}
														/>
													)
											)
										)}
									</Col>
								</Row>,
						  ]
						: null}
				</Col>
			}
			onMouseEnter={collectContacts}
			placement={'bottom'}
		>
			<Row
				style={{
					display: 'inline-flex',
					alignSelf: 'center',
					backgroundColor: 'rgb(235,235,235)',
					borderRadius: 16,
					padding: 0,
					paddingLeft: 8,
				}}
			>
				<span style={{ marginTop: 4, fontSize: 20 }}>
					<i style={{ color: '#8C8C8C' }} className="icons8-font icons8-street-worker" />
				</span>
				<Avatar.Group
					maxCount={3}
					maxPopoverTrigger="hover"
					size="small"
					maxStyle={{
						display: 'none',
						color: '#333',
						backgroundColor: 'transparent',
						cursor: 'pointer',
					}}
				>
					{emails.map((email, i) => {
						const name = nullSafeGetOrElse(`${i}`, names, ``).split(' ');
						const nameGiven = name && name[0] ? name[0] : '';
						const nameFamily = name && name[1] ? name[1] : '';
						return (
							<ContactAvatar
								key={`${i}-contact-avatar`}
								hidePopover={true}
								placement="bottomRight"
								userType={'buyer'}
								size={28}
								style={{
									fontSize: 12,
									zIndex: emails.length - i,
									position: 'relative',
								}}
								contact={
									contacts && contacts[email] ? contacts[email] : { email, nameGiven, nameFamily }
								}
							/>
						);
					})}
				</Avatar.Group>
				{nullSafeGetOrElse('length', emails, 0) > 3 ? (
					<span
						style={{
							fontSize: 14,
							alignSelf: 'center',
							marginLeft: 2,
							marginRight: 6,
							marginTop: 2,
							color: BLACKS_AND_WHITES.blackPrimary,
						}}
					>
						+{nullSafeGetOrElse('length', emails, 0) - 3}
					</span>
				) : null}
			</Row>
		</Popover>
	);
};

const mapStateToProps = (state, ownProps) => ({
	allContacts: state.all_contacts.records,
	history: ownProps.history,
	companyConfig: state.company_config,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	getContacts: (ids) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? contactsRestCrudThunksForSupplier.readMultiple(
						ids,
						'workOrdersIndex',
						null,
						null,
						null,
						true
				  )
				: contactsRestCrudThunksForBuyer.readMultiple(
						ids,
						'workOrdersIndex',
						null,
						null,
						null,
						true
				  )
		),
});

export default withUserType(
	connect(mapStateToProps, mapDispatchToProps)(WorkOrderTechAssignedPill)
);
